import { http } from '@/lib/http';
import type {
  CancelTripResult,
  DispatchTripsInput,
  DispatchTripsResult,
  TripView,
  TripsPage,
} from './types';

// The three operator-bff ground-transport routes (feat/transport §7, slice T4).
// `http` is the single configured ky instance (Auth0 token + traceparent + 401
// handling) per frontend-conventions §6.4 — never a bare fetch.
//
// NOTE the missing leading slash: ky's `prefixUrl` requires relative paths.

/**
 * `operator-bff:GET /v1/cases/{caseUrn}/trips@v1` — every trip of a case.
 *
 * Unwraps the `{ items }` envelope to a plain array so the WS merge in
 * `applyEvent` has one shape to reason about, and tolerates a null/absent
 * `items` (a case the domain has never seen) as an empty list rather than
 * throwing the card into its error state for what is really "nothing yet".
 */
export async function fetchCaseTrips(caseUrn: string): Promise<TripView[]> {
  const page = await http
    .get(`v1/cases/${encodeURIComponent(caseUrn)}/trips`)
    .json<TripsPage | null>();
  return Array.isArray(page?.items) ? page.items : [];
}

/**
 * `operator-bff:POST /v1/transport/dispatch@v1` — MANUAL dispatch, one trip per
 * passenger. 202: the domain accepts the intent and calls the vendor
 * asynchronously, so the returned trips are REQUESTED, not DISPATCHED. Idempotent
 * per `(caseUrn, passengerUrn, leg)` — a passenger who already has a live trip on
 * that leg gets it back rather than a second car, which is what makes the
 * re-dispatch affordance safe to double-click.
 */
export async function dispatchTrips(input: DispatchTripsInput): Promise<TripView[]> {
  const result = await http
    .post('v1/transport/dispatch', { json: input })
    .json<DispatchTripsResult | null>();
  return Array.isArray(result?.trips) ? result.trips : [];
}

/**
 * `operator-bff:POST /v1/transport/trips/{urn}/cancel@v1`. `reason` is mandatory
 * at the BFF edge (400 `reason_required` otherwise) — it is the audit trail.
 */
export async function cancelTrip(urn: string, reason: string): Promise<TripView> {
  const result = await http
    .post(`v1/transport/trips/${encodeURIComponent(urn)}/cancel`, { json: { reason } })
    .json<CancelTripResult>();
  return result.trip;
}
